import React, {
  lazy, Suspense, useEffect, useMemo, useState
} from 'react';
import classNames from 'classnames';
import proptypes from 'prop-types';
import { Button, LoadingButton } from '@one-thd/sui-atomic-components';
import { useImpression } from '@thd-olt-component-react/impression';
import { useStore } from '@thd-nucleus/experience-context';
import { KidsWorkshopsRegistration } from '@thd-olt-component-react/kids-workshops-registration';
import { VideoHeroImage } from './VideoHeroImage';
import { BLACK_CTA_BTN_CLASSES, COLORS, ORANGE_CTA_BTN_CLASSES, WHITE_CTA_BTN_CLASSES } from '../constants';
import { getTarget } from '../utils/getTarget';
import { useGoogleGardenAgent } from '../hooks/useGoogleGardenAgent';
import { appendUrlParam } from '../utils/appendUrlParam';
import './hero.scss';

const { string, bool, number, shape } = proptypes;

const RisDrawer = lazy(() => import(
  /* webpackChunkName: "reverse-image-search" */ '@thd-olt-component-react/reverse-image-search'
)
  .then(module => ({ default: module.RisDrawer }))
);

const BrandHeroRenderer = ({
  componentId,
  componentClass,
  withSideNav = undefined,
  componentPosition = 1,
  dataComponent = {},
  cmMmcParam = '',
}) => {
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [isOrangeLens, setIsOrangeLens] = useState(false);
  const [isGoogleGardenAgentLoaded, setGoogleGardenAgentLoaded] = useState(false);
  const {
    eyebrow,
    title,
    description,
    cta,
    link,
    previewImage,
    textColor,
    buttonColor,
    logoOrBadge,
    altText,
    altTextForLogoBadge,
    overrideAction
  } = dataComponent || {};
  const isGoogleGardenAgent = link?.includes?.('googlegardenagent');

  const { storeId } = useStore();

  const backgroundColor = dataComponent?.backgroundColor || COLORS.white;

  const heroImgSrc = previewImage?.damDownloadedContent?.url
    || previewImage?.damContentSelector?.assetData[0]?.selectedImageUrl;

  const [openChat] = useGoogleGardenAgent();

  useEffect(() => {
    // Define the event handler function
    const handleGoogleAgentLoaded = () => {
      setGoogleGardenAgentLoaded(true);
    };
    window.addEventListener('google-agent-loaded', handleGoogleAgentLoaded);
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('hero.ready');
    return () => {
      window.removeEventListener('google-agent-loaded', handleGoogleAgentLoaded);
    };
  }, []);
  const getImageType = (url) => {
    if (!url) return '';
    const extension = url.split('.').pop().toLowerCase().split('?')[0];
    return ['mp4', 'gif'].includes(extension) ? extension : '';
  };

  const imageType = getImageType(heroImgSrc);

  const { ref, clickID } = useImpression({
    data: {
      id: componentId,
      name: 'HeroFlattenImage',
      component: componentClass,
      position: componentPosition,
      type: 'content',
      category: imageType,
    },
  });

  const brandLogoSrc = logoOrBadge?.damDownloadedContent?.url
    || logoOrBadge?.damContentSelector?.assetData[0]?.selectedImageUrl;

  const colourRegex = /#[0-9A-F]{6}/;
  const textColorHex = textColor?.match(colourRegex)[0] || COLORS.black;
  const ctaBackgroundColorHex = buttonColor?.match(colourRegex)[0] || COLORS.orange;

  const buttonColourStyles = useMemo(() => {
    return {
      [ORANGE_CTA_BTN_CLASSES]: ctaBackgroundColorHex === COLORS.orange,
      [BLACK_CTA_BTN_CLASSES]: ctaBackgroundColorHex === COLORS.black,
      [WHITE_CTA_BTN_CLASSES]: ctaBackgroundColorHex === COLORS.white,
    };
  }, [ctaBackgroundColorHex]);

  const [isKidsWorkshopOpen, setIsKidsWorkshopOpen] = useState(false);
  const shouldOpenKidsWorkshop = overrideAction === 'Open Workshops Registration Drawer';

  const toggleKidsWorkshopDrawer = (open) => () => {
    setIsKidsWorkshopOpen(open);
  };

  const openKidsWorkshopDrawer = () => {
    setIsKidsWorkshopOpen(true);
  };

  const getPriority = (position) => {
    return position === 1
      ? { fetch: 'high', loading: 'eager' }
      : { fetch: 'low', loading: 'lazy' };
  };

  const { fetch: fetchPriorityValue, loading: loadingValue } = getPriority(componentPosition);

  const isStoreSelected = !!storeId;
  useEffect(() => {
    if (typeof window !== 'undefined' && link) {
      try {
        const url = new URL(link);
        const params = new URLSearchParams(url.search);
        setIsOrangeLens(!!params.get('orangelens'));
      } catch (error) {
        // Silently handle URL parsing errors
        setIsOrangeLens(false);
      }
    }
  }, [link]);

  const handleClick = () => {
    setIsDrawerVisible(true);
  };

  return (
    <div
      ref={ref}
      /* eslint-disable-next-line react/no-unknown-property */
      clickid={clickID}
      id={`${componentClass}-${componentId}`}
      data-component-id={componentId}
      data-component-name={componentClass}
      data-testid="brand-hero-renderer"
      className={classNames(
        'brand-hero__container sui-w-full sui-h-full',
        'sui-border-1 sui-border-solid sui-border-primary sui-rounded-base'
      )}
    >
      <div
        className={
          classNames('brand-hero__info-box sui-max-w-full sui-gap-2',
            'sui-flex sui-flex-col sui-justify-center sui-p-8 xl:sui-p-6',
            { 'brand-hero-w-sidenav__info-box': withSideNav }
          )
        }
        style={{
          color: textColorHex,
          backgroundColor,
        }}
      >
        <p className="sui-text-base sui-font-bold">{eyebrow}</p>
        {
          logoOrBadge
          && (
            <img
              className={classNames(
                'brand-hero__logo sui-w-auto sui-max-h-12 sui-object-contain sui-object-left'
              )}
              alt={altTextForLogoBadge}
              src={brandLogoSrc}
              fetchPriority={fetchPriorityValue}
              loading={loadingValue}
              width="1"
              height="1"
            />
          )
        }
        <h2
          className="sui-h2-display sui-uppercase"
          style={{
            color: textColorHex,
          }}
        >
          {title}
        </h2>
        <p className="brand-hero__subtitle sui-text-base sui-font-normal">{description}</p>
        {!isOrangeLens && !shouldOpenKidsWorkshop && !isGoogleGardenAgent && link && cta && (
          // eslint-disable-next-line react/jsx-no-target-blank
          <a
            href={appendUrlParam(link, cmMmcParam)}
            target={getTarget(link)}
            className={classNames(
              'sui-btn sui-w-fit sui-font-bold sui-text-base',
              'sui-leading-normal sui-tracking-normal sui-normal-case',
              'sui-line-clamp-unset sui-btn-primary sui-btn-focus sui-mt-2 link--hover-no-underline',
              buttonColourStyles,
            )}
            aria-label={`Navigate to ${title}`}
          >
            {cta}
          </a>
        )}
        {!isOrangeLens && !shouldOpenKidsWorkshop && isGoogleGardenAgent && (
          <>
            <LoadingButton
              variant="primary"
              onClick={openChat}
              data-contentful-field-id="cta-default"
              data-contentful-entry-id={componentId}
              aria-label={`Navigate to ${title}`}
              loading={!isGoogleGardenAgentLoaded}
            >
              {cta}
            </LoadingButton>
          </>
        )}
        {isOrangeLens && !shouldOpenKidsWorkshop && !isGoogleGardenAgent && (
          <>
            <Button
              variant="primary"
              onClick={handleClick}
              data-contentful-field-id="cta-default"
              data-contentful-entry-id={componentId}
              aria-label={`Navigate to ${title}`}
              type="button"
            >
              {cta}
            </Button>
            {isDrawerVisible && (
              <Suspense fallback={<div />}>
                <RisDrawer open={isDrawerVisible} handleDrawerClose={() => setIsDrawerVisible(false)} />
              </Suspense>
            )}
          </>
        )}
        {!isOrangeLens && shouldOpenKidsWorkshop && !isGoogleGardenAgent && (
          isStoreSelected ? (
            <Button
              aria-label={`Register for ${title}`}
              onClick={openKidsWorkshopDrawer}
              title={cta}
            >
              {cta}
            </Button>
          ) : (
            <span>To register for a Kids Workshop, please select your store</span>
          )
        )}
      </div>
      {shouldOpenKidsWorkshop && storeId && (
        <KidsWorkshopsRegistration
          isDrawerOpen={isKidsWorkshopOpen}
          toggleDrawer={toggleKidsWorkshopDrawer}
          oldStoreId={storeId}
        />
      )}
      {imageType === 'mp4'
        ? (
          <VideoHeroImage
            imageUrl={heroImgSrc}
            altText={altText}
            fullWidth={false}
            brandHero
            videoClasses={classNames(
              'sui-h-auto sui-w-full sui-object-cover sui-aspect-video sui-row-start-1',
              'md:sui-aspect-square md:sui-w-auto md:sui-h-full brand-hero__image',
              { 'lg:sui-aspect-square 2xl:sui-aspect-video': withSideNav }
            )}
          />
        ) : (
          <img
            className={classNames(
              'sui-h-auto sui-w-full sui-object-cover sui-aspect-video sui-row-start-1',
              'md:sui-aspect-square md:sui-w-auto md:sui-h-full brand-hero__image',
              { 'lg:sui-aspect-square 2xl:sui-aspect-video': withSideNav }
            )}
            alt={altText || `Image for ${title}`}
            src={heroImgSrc}
            fetchPriority={fetchPriorityValue}
            loading={loadingValue}
            width="1"
            height="1"
          />
        )}
    </div>
  );
};

BrandHeroRenderer.propTypes = {
  componentId: string.isRequired,
  componentClass: string.isRequired,
  withSideNav: bool,
  componentPosition: number,
  dataComponent: shape({}),
  cmMmcParam: string,
};

BrandHeroRenderer.displayName = 'BrandHeroRenderer';

export { BrandHeroRenderer };
